import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import { REACT_APP_API_BASE_URL } from "../../config";
import { ROUTES } from "../../routes/User/paths";

export default function GuestSpectateDashboard() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    let mounted = true;

    const loadRooms = async () => {
      try {
        const res = await fetch(`${REACT_APP_API_BASE_URL}/chat-rooms/rooms`);
        if (!res.ok) {
          throw new Error("Unable to fetch rooms");
        }
        const payload = await res.json();
        if (!mounted) return;
        setRooms(payload?.data?.data || payload?.data || []);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        setError("Could not load rooms for guest spectating.");
        setLoading(false);
      }
    };

    loadRooms();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredRooms = useMemo(() => {
    if (!searchTerm.trim()) return rooms;
    const q = searchTerm.toLowerCase();
    return rooms.filter((room) => {
      const roomName = String(room?.name || "").toLowerCase();
      const host = String(room?.admin_username || "").toLowerCase();
      return roomName.includes(q) || host.includes(q);
    });
  }, [rooms, searchTerm]);

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #eef6ff 0%, #ffffff 45%)" }}>
      <PublicHeader scrolled />

      <Container maxWidth="lg" sx={{ pt: 12, pb: 8 }}>
        <Stack spacing={3}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                Spectate Live Debates
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Guest mode is view-only. Log in to access live chat and full spectator features.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by room topic or host"
                  size="small"
                />
                <Button variant="contained" onClick={() => history.push(ROUTES.login)}>
                  Log In
                </Button>
                <Button variant="outlined" onClick={() => history.push(ROUTES.register)}>
                  Create Account
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <Stack spacing={2} alignItems="center" sx={{ py: 10 }}>
              <CircularProgress />
              <Typography>Loading rooms...</Typography>
            </Stack>
          ) : error ? (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Rooms Unavailable
                </Typography>
                <Typography color="text.secondary">{error}</Typography>
              </CardContent>
            </Card>
          ) : filteredRooms.length === 0 ? (
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  No Rooms Found
                </Typography>
                <Typography color="text.secondary">
                  Try a different search, or come back once more rooms are live.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {filteredRooms.map((room) => (
                <Grid item xs={12} md={6} key={room.id}>
                  <Card sx={{ borderRadius: 3, height: "100%" }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {room.name}
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            Host: {room.admin_username || "Unknown"}
                          </Typography>
                        </Box>
                        <Chip size="small" label={`${room.total_participants || 0} users`} />
                      </Stack>
                      <Typography color="text.secondary" sx={{ mt: 1 }}>
                        {room.description || room.room_description || "Live discussion room"}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => history.push(`${ROUTES.spectateGuest}/${room.id}`)}
                      >
                        Guest View
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => history.push(`${ROUTES.spectate}/${room.id}`)}
                      >
                        Spectate + Chat
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
